import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type {
  BulkRouteInput,
  BulkSortingWarehouseInput,
  SortingDecisionDto,
  SortingDecisionCreateInput,
  SortingPackageDto,
  SortingPackageFilters,
} from './types';

export interface SortingListMeta {
  total: number;
  page: number;
  per_page: number;
}

// ---------------------------------------------------------------------------
// New per-package sorting endpoints (the 分類 page that spans many lots).
// ---------------------------------------------------------------------------

export const fetchSortingPackages = async (
  params: SortingPackageFilters = {},
): Promise<{ data: SortingPackageDto[]; meta: SortingListMeta }> => {
  const res = await apiClient.get<ApiSuccess<SortingPackageDto[], SortingListMeta>>(
    `/sorting-packages`,
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const bulkRouteAssign = async (body: BulkRouteInput): Promise<{ updated: number }> => {
  const res = await apiClient.post<ApiSuccess<{ updated: number }>>(
    `/sorting-packages/bulk-route`,
    body,
  );
  return res.data.data;
};

export const bulkSortingWarehouse = async (
  body: BulkSortingWarehouseInput,
): Promise<{ updated: number }> => {
  const res = await apiClient.post<ApiSuccess<{ updated: number }>>(
    `/sorting-packages/bulk-warehouse`,
    body,
  );
  return res.data.data;
};

// ---------------------------------------------------------------------------
// Legacy sorting_decisions endpoints — retained for back-compat with the
// older lot-level callers and the test fixture.
// ---------------------------------------------------------------------------

export const fetchSortingDecisions = async (
  batchId: string,
  params: { page?: number; per_page?: number } = {},
): Promise<{ data: SortingDecisionDto[]; meta: SortingListMeta }> => {
  const res = await apiClient.get<ApiSuccess<SortingDecisionDto[], SortingListMeta>>(
    `/monthly-batches/${batchId}/sorting-decisions`,
    { params },
  );
  const data = res.data.data ?? [];
  const meta = res.data.meta ?? {
    total: data.length,
    page: params.page ?? 1,
    per_page: params.per_page ?? data.length,
  };
  return { data, meta };
};

export const createSortingDecision = async (
  lotId: string,
  body: SortingDecisionCreateInput,
): Promise<SortingDecisionDto> => {
  const res = await apiClient.post<ApiSuccess<SortingDecisionDto>>(
    `/inbound-lots/${lotId}/sorting-decision`,
    body,
  );
  return res.data.data;
};

export const confirmSortingDecision = async (id: string): Promise<void> => {
  await apiClient.post(`/sorting-decisions/${id}/confirm`);
};

export const batchCreateSortingDecisions = async (
  batchId: string,
  body: { route_type: SortingDecisionCreateInput['route_type']; decision_reason?: string },
): Promise<void> => {
  await apiClient.post(`/monthly-batches/${batchId}/sorting-decisions:batch`, body);
};
